import React, { useState, useMemo } from 'react';
import { string } from 'prop-types';
import './styles/styles.scss';
import Button from '../../shared/Button';
import GiftCard from '../GiftCard';
import ProgressDialog from '../../shared/ProgressDialog';
import { StringConstantsMixin } from '../../../application/javascripts/react_components/mixins/shared/string_constants_mixin';
import { LanguageSupportUtil } from '../../../utils/language_support_util';
import { retrievePrintedGift } from '../../../utils/requests/gifting_requests';
import { EN_US } from '../../../utils/constants/LanguageRegionCode';

const {
  GIFT_FLOW: {
    PRINT: {
      NEW_TO_ALLTRAILS,
      CREATE_ACCOUNT,
      REDEEM_GIFT,
      ALREADY_HAVE_ONE,
      REDEMPTION_CODE,
      YOUR_GIFT_SUBSCRIPTION,
      ANNUAL_PRO_PLAN,
      THREE_YEAR_PRO_PLAN
    },
    GIFT_CUSTOMIZE_FORM: { TO, FROM, PRINT }
  }
} = StringConstantsMixin.strings;

const NameAndMessage = ({ message, senderName, recipientName }) => {
  return (
    <>
      <div className="senderRecipient">
        <span>{TO}:</span> {recipientName}
      </div>
      <div className="giftMessage">{message}</div>
      <div className="senderRecipient">
        <span>{FROM}: </span> {senderName}
      </div>
    </>
  );
};

const parseStringForComponent = (text, link) => {
  const LINK_COMPONENT = 'LINK_COMPONENT';

  return text(LINK_COMPONENT)
    .split(LINK_COMPONENT)
    .map((str, idx) => {
      if (idx === 0) {
        return (
          <span>
            {str} {link}
          </span>
        );
      }

      return <span>{str}</span>;
    });
};

const GiftCardPrint = ({ id, code, planType, recipientName, senderName, message, background, languageRegionCode }) => {
  const [printingState, updatePrintingState] = useState(null);

  const handlePrintSuccess = data => {
    const downloadLink = document.createElement('a');
    downloadLink.href = data.download_url;
    document.body.appendChild(downloadLink);
    downloadLink.click();
    updatePrintingState(null);
  };

  const handlePrintError = err => {
    updatePrintingState('error');
  };

  const printGiftCard = () => {
    updatePrintingState('loading');
    retrievePrintedGift(id, languageRegionCode)
      .then(handlePrintSuccess)
      .catch(handlePrintError);
  };

  const [signUpText, redeemText] = useMemo(() => {
    const shortRegionCode = languageRegionCode === 'en-US' ? '' : `/${languageRegionCode.split('-')[0]}`;

    const signUpLink = () => {
      return <a href={LanguageSupportUtil.wrapUrlSafe('/signup', languageRegionCode)}>{`www.alltrails.com${shortRegionCode}/signup`}</a>;
    };

    const redeemLink = () => {
      return <a href={LanguageSupportUtil.wrapUrlSafe(`/redeem?k=${code}`, languageRegionCode)}>{`www.alltrails.com${shortRegionCode}/redeem`}</a>;
    };

    const createAccount = parseStringForComponent(CREATE_ACCOUNT, signUpLink());
    const redeemGift = parseStringForComponent(REDEEM_GIFT, redeemLink());

    const signup = (
      <>
        <span className="bold">{NEW_TO_ALLTRAILS} </span>
        {createAccount}
      </>
    );

    const redeem = (
      <>
        <span className="bold">{ALREADY_HAVE_ONE} </span>
        {redeemGift}
      </>
    );

    return [signup, redeem];
  }, []);

  return (
    <div className="giftCardPrint">
      <div className="printContainer">
        <div className="cardAndData">
          <div className="cardContainer">
            <div className="card">
              <GiftCard backgroundId={background} classes="giftCardCard" />
              <div className="inMobile">
                <NameAndMessage message={message} senderName={senderName} recipientName={recipientName} />
              </div>
            </div>
            <div className="cardMeta">
              <div className="planType">{planType === 'annual' ? ANNUAL_PRO_PLAN : THREE_YEAR_PRO_PLAN}</div>
              <div className="redemptionCode">
                {REDEMPTION_CODE}: <span>{code}</span>
              </div>
            </div>
          </div>
          <div className="metadata">
            <div className="yourGiftCard">{YOUR_GIFT_SUBSCRIPTION}</div>
            <div className="desktop">
              <NameAndMessage message={message} senderName={senderName} recipientName={recipientName} />
            </div>
          </div>
        </div>
        <div className="subTextContainer">
          <div className="subText">{signUpText}</div>
          <div className="subText">{redeemText}</div>
        </div>
      </div>
      <Button onClick={printGiftCard} variant="primary" classes="button">
        {PRINT}
      </Button>
      {printingState && <ProgressDialog variant={printingState} updateProgressDialog={updatePrintingState} />}
    </div>
  );
};

GiftCardPrint.propTypes = {
  code: string.isRequired,
  planType: string.isRequired,
  senderName: string.isRequired,
  recipientName: string.isRequired,
  message: string.isRequired,
  background: string.isRequired,
  languageRegionCode: string
};

GiftCardPrint.defaultProps = {
  languageRegionCode: EN_US
};

export default GiftCardPrint;
